<script>
    import { onMount, onDestroy, tick } from 'svelte';
    import { get } from 'svelte/store';
    import { fetchEgresoDrops } from '../../controllers/egresoDropController.js';
    import { fetchEgresoDropEscoltas } from '../../controllers/egresoDropEscoltaController.js';
    import { fetchEgresoDropFiles } from '../../controllers/egresoDropFilesController.js';
    import { saveEgresoDrop } from '../../controllers/egresoDropController.js';
    import { authStore } from '../../lib/authStore';
    import pb from '../../lib/pocketbase';
    
    export let selectedItem;
    export let deliveries;
    export let transportes;
    export let vehiculos;
    export let escoltas;

    let deliveryMap;
    let deliveryMarker;
    let mapElement;
    let egresoDrops = [];
    let selectedDrop = null;
    let dropFiles = {};
    let isLoading = true;
    let refreshInterval = null;
    let isAdmin = false; // Track if current user is admin

    // Check admin role from localStorage
    function checkAdminRole() {
        const roleData = localStorage.getItem("pb_user_role");
        if (roleData) {
            try {
                const role = JSON.parse(roleData);
                isAdmin = role.descrip === 'Administrador';
            } catch (error) {
                isAdmin = false;
            }
        } else {
            isAdmin = false;
        }
    }

    // Map styles for better visual appearance
    const mapStyles = [
        {
            "featureType": "all",
            "elementType": "geometry.fill",
            "stylers": [{"weight": "2.00"}]
        },
        {
            "featureType": "all",
            "elementType": "geometry.stroke",
            "stylers": [{"color": "#9c9c9c"}]
        },
        {
            "featureType": "all",
            "elementType": "labels.text",
            "stylers": [{"visibility": "on"}]
        },
        {
            "featureType": "landscape",
            "elementType": "all",
            "stylers": [{"color": "#f2f2f2"}]
        },
        {
            "featureType": "landscape",
            "elementType": "geometry.fill",
            "stylers": [{"color": "#ffffff"}]
        },
        {
            "featureType": "landscape.man_made",
            "elementType": "geometry.fill",
            "stylers": [{"color": "#ffffff"}]
        },
        {
            "featureType": "poi",
            "elementType": "all",
            "stylers": [{"visibility": "off"}]
        },
        {
            "featureType": "road",
            "elementType": "all",
            "stylers": [{"saturation": -100}, {"lightness": 45}]
        },
        {
            "featureType": "road",
            "elementType": "geometry.fill",
            "stylers": [{"color": "#eeeeee"}]
        },
        {
            "featureType": "road",
            "elementType": "labels.text.fill",
            "stylers": [{"color": "#7b7b7b"}]
        },
        {
            "featureType": "road",
            "elementType": "labels.text.stroke",
            "stylers": [{"color": "#ffffff"}]
        },
        {
            "featureType": "road.highway",
            "elementType": "all",
            "stylers": [{"visibility": "simplified"}]
        },
        {
            "featureType": "road.arterial",
            "elementType": "labels.icon",
            "stylers": [{"visibility": "off"}]
        },
        {
            "featureType": "transit",
            "elementType": "all",
            "stylers": [{"visibility": "off"}]
        },
        {
            "featureType": "water",
            "elementType": "all",
            "stylers": [{"color": "#46bcec"}, {"visibility": "on"}]
        },
        {
            "featureType": "water",
            "elementType": "geometry.fill",
            "stylers": [{"color": "#c8d7d4"}]
        },
        {
            "featureType": "water",
            "elementType": "labels.text.fill",
            "stylers": [{"color": "#070707"}]
        },
        {
            "featureType": "water",
            "elementType": "labels.text.stroke",
            "stylers": [{"color": "#ffffff"}]
        }
    ];

    // Load egreso drops for the selected item
    async function loadEgresoDrops() {
        if (!selectedItem?.id) return;

        try {
            isLoading = true;
            egresoDrops = await fetchEgresoDrops(selectedItem.id);
            console.log("Loaded egreso drops:", egresoDrops);

            // Load escoltas and files for each drop
            for (let drop of egresoDrops) {
                try {
                    drop.escoltas = await fetchEgresoDropEscoltas(drop.id);
                    dropFiles[drop.id] = await fetchEgresoDropFiles(drop.id);
                } catch (error) {
                    console.error(`Error loading data for drop ${drop.id}:`, error);
                    drop.escoltas = [];
                    dropFiles[drop.id] = [];
                }
            }

            // Select the first drop by default if available
            selectedDrop = null;
            await tick();
            selectedDrop = egresoDrops[0];
            initializeDeliveryMap();
        } catch (error) {
            console.error("Error loading egreso drops:", error);
            egresoDrops = [];
        } finally {
            isLoading = false;
        }
    }

    // Initialize delivery map for selected drop
    function initializeDeliveryMap() {
        if (!selectedDrop) return;

        try {
            let deliveryRef = selectedDrop.geo_delivery_ref || selectedDrop.geo_target_ref;
            if (deliveryRef) {
                const [lat, lng] = deliveryRef.split(',').map(parseFloat);
                if (!isNaN(lat) && !isNaN(lng)) {
                    initMap('delivery-map-canvas', lat, lng, true);
                } else {
                    console.warn('Invalid coordinates in geo_ref:', deliveryRef);
                    initMap('delivery-map-canvas', 14.603032776408446, -90.53445609626046, true);
                }
            } else {
                initMap('delivery-map-canvas', 14.603032776408446, -90.53445609626046, true);
            }
        } catch (error) {
            console.error('Error initializing delivery map:', error);
            initMap('delivery-map-canvas', 14.603032776408446, -90.53445609626046, true);
        }
    }

    // Initialize a Google Map
    function initMap(mapId, lat, lng, isDelivery = false) {
        const google = window.google;
        if (!google) {
            console.error('Google Maps not loaded');
            return;
        }

        const mapElement = document.getElementById(mapId);
        if (!mapElement) {
            console.error('Map canvas element not found:', mapId);
            return;
        }

        // Clear existing map if any
        if (deliveryMap) {
            google.maps.event.clearInstanceListeners(deliveryMap);
            deliveryMap = null;
        }
        if (deliveryMarker) {
            deliveryMarker.setMap(null);
            deliveryMarker = null;
        }

        const myLatlng = new google.maps.LatLng(lat, lng);
        const mapOptions = {
            zoom: 16,
            scrollwheel: true,
            center: myLatlng,
            mapTypeId: google.maps.MapTypeId.ROADMAP,
            styles: mapStyles,
            zoomControl: true,
            mapTypeControl: false,
            scaleControl: true,
            streetViewControl: false,
            rotateControl: false,
            fullscreenControl: true
        };

        const mapInstance = new google.maps.Map(mapElement, mapOptions);
        const markerInstance = new google.maps.Marker({
            position: myLatlng,
            map: mapInstance,
            animation: google.maps.Animation.DROP,
            title: isDelivery ? "Ubicación de entrega" : "Ubicación destino",
            draggable: isAdmin,
            icon: {
                url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                    <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="16" cy="16" r="12" fill="#4285F4" stroke="#FFFFFF" stroke-width="2"/>
                        <circle cx="16" cy="16" r="4" fill="#FFFFFF"/>
                    </svg>
                `),
                scaledSize: new google.maps.Size(32, 32),
                anchor: new google.maps.Point(16, 16)
            }
        });

        if (isDelivery) {
            deliveryMap = mapInstance;
            deliveryMarker = markerInstance;
        }

        // Add event listeners
        google.maps.event.addListener(markerInstance, 'dragend', async function(event) {
            // Only allow saving if user is admin
            if (!isAdmin) {
                return;
            }
            
            const position = event.latLng;
            console.log('Marker dragged to:', position.lat(), position.lng());
            
            // Save the new coordinates to the selected drop
            if (selectedDrop) {
                const geo_ref = `${position.lat()},${position.lng()}`;
                try {
                    await saveEgresoDrop({ 
                        ...selectedDrop, 
                        geo_delivery_ref: geo_ref 
                    }, selectedDrop.id);
                    selectedDrop.geo_delivery_ref = geo_ref;
                } catch (error) {
                    console.error('Error saving location:', error);
                }
            }
        });

        google.maps.event.addListener(mapInstance, 'click', async function(event) {
            // Only allow clicking to move marker if user is admin
            if (!isAdmin) {
                return;
            }
            
            const position = event.latLng;
            markerInstance.setPosition(position);
            console.log('Map clicked at:', position.lat(), position.lng());
            
            // Save the new coordinates to the selected drop
            if (selectedDrop) {
                const geo_ref = `${position.lat()},${position.lng()}`;
                try {
                    await saveEgresoDrop({ 
                        ...selectedDrop, 
                        geo_delivery_ref: geo_ref 
                    }, selectedDrop.id);
                    selectedDrop.geo_delivery_ref = geo_ref;
                } catch (error) {
                    console.error('Error saving location:', error);
                }
            }
        });

        return { map: mapInstance, marker: markerInstance };
    }

    // Get escolta names for display
    function getEscoltaNames(dropEscoltas) {
        if (!dropEscoltas || !Array.isArray(dropEscoltas) || dropEscoltas.length === 0) {
            return 'Ninguno seleccionado';
        }

        const names = dropEscoltas
            .map(e => {
                // Access the expanded escolta data correctly
                if (e.expand?.escolta?.nombre) {
                    return e.expand.escolta.nombre;
                }
                // If it's a direct escolta object
                if (e.nombre) {
                    return e.nombre;
                }
                // If it's an ID, try to find in escoltas array
                if (typeof e === 'string' && escoltas) {
                    const escolta = escoltas.find(esc => esc.id === e);
                    return escolta?.nombre || null;
                }
                return null;
            })
            .filter(Boolean)
            .join(', ');

        return names || 'Ninguno seleccionado';
    }

    // Helper function to get vehicle info
    function getVehicleInfo(drop) {
        if (!drop?.vehiculo || !vehiculos) return 'No seleccionado';
        
        const vehicle = vehiculos.find(v => v.id === drop.vehiculo);
        if (!vehicle) return 'No seleccionado';
        
        return `${vehicle.placa} / ${vehicle.marca} ${vehicle.modelo} (${vehicle.color})`;
    }

    // Helper function to get delivery info
    function getDeliveryInfo(drop) {
        if (!drop?.delivery || !deliveries) return 'No seleccionado';
        
        const delivery = deliveries.find(d => d.id === drop.delivery);
        return delivery?.nombre || 'No seleccionado';
    }

    // Helper function to get transport info
    function getTransportInfo(drop) {
        if (!drop?.transporte || !transportes) return 'No seleccionado';
        
        const transport = transportes.find(t => t.id === drop.transporte);
        return transport?.descrip || 'No seleccionado';
    }

    // Handle drop selection
    function selectDrop(drop) {
        selectedDrop = drop;
        initializeDeliveryMap();
    }

    // Get file count for a drop
    function getFileCount(dropId) {
        return dropFiles[dropId]?.length || 0;
    }

    onMount(() => {
        checkAdminRole();
        // Check again after a short delay in case role was still loading
        setTimeout(checkAdminRole, 1000);
        
        if (selectedItem) {
            loadEgresoDrops();
        }
    });

    onDestroy(() => {
        if (deliveryMarker) {
            deliveryMarker.setMap(null);
        }
        if (deliveryMap) {
            google.maps.event.clearInstanceListeners(deliveryMap);
        }
    });
</script>

<div class="space-y-6">
    {#if isLoading}
        <div class="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 animate-pulse">
            <div class="flex items-center justify-center py-8">
                <div class="animate-spin rounded-full h-10 w-10 border-b-4 border-blue-500"></div>
                <span class="ml-4 text-lg text-gray-700 font-semibold">Cargando drops de entrega...</span>
            </div>
        </div>
    {:else if egresoDrops.length === 0}
        <div class="bg-gradient-to-r from-blue-50 to-green-50 rounded-2xl shadow-lg border border-gray-100 p-8">
            <div class="text-center py-8">
                <div class="text-gray-300 mb-4">
                    <svg class="mx-auto h-14 w-14" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-1.447-.894L15 4m0 13V4m-6 3l6-3"/>
                    </svg>
                </div>
                <h3 class="text-2xl font-bold text-gray-800 mb-2">No hay drops configurados</h3>
                <p class="text-gray-500">No se han configurado drops de entrega para este egreso.</p>
            </div>
        </div>
    {:else}
        <!-- Drop Selection -->
        <div class="bg-gradient-to-r from-blue-50 to-green-50 rounded-2xl shadow-lg border border-gray-100 p-8">
            <div class="flex items-center justify-between mb-6">
                <h3 class="text-2xl font-bold text-gray-800">Drops de Entrega <span class='text-blue-500'>({egresoDrops.length})</span></h3>
            </div>
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                {#each egresoDrops as drop, index}
                    <div 
                        class="p-6 border-2 rounded-2xl cursor-pointer transition-all duration-200 shadow-md hover:shadow-xl hover:scale-[1.03] {selectedDrop?.id === drop.id ? 'border-blue-500 bg-white' : 'border-gray-200 bg-gray-50'}"
                        on:click={() => selectDrop(drop)}
                    >
                        <div class="flex items-center justify-between mb-2">
                            <span class="inline-block px-3 py-1 text-xs font-bold rounded-full 
                                {drop.delivery_method === 'Despacho' ? 'bg-blue-200 text-blue-900' : 
                                 drop.delivery_method === 'Repartidor' ? 'bg-green-200 text-green-900' : 
                                 'bg-purple-200 text-purple-900'}">
                                {drop.delivery_method}
                            </span>
                            <span class="text-xs text-gray-400 font-mono">#{index + 1}</span>
                        </div>
                        {#if drop.target_ref}
                            <p class="text-base font-semibold text-gray-900 mb-1 truncate">{drop.target_ref}</p>
                        {/if}
                        <div class="text-xs text-gray-600 space-y-1">
                            {#if drop.delivery_method === 'Repartidor' && drop.expand?.delivery}
                                <p>Repartidor: <span class="font-semibold text-green-800">{drop.expand.delivery.nombre}</span></p>
                            {/if}
                            {#if drop.delivery_method === 'Transporte' && drop.expand?.transporte}
                                <p>Transporte: <span class="font-semibold text-purple-800">{drop.expand.transporte.descrip}</span></p>
                            {/if}
                            {#if drop.escoltas && drop.escoltas.length > 0}
                                <p>Escoltas: <span class="font-semibold text-blue-800">{drop.escoltas.length}</span></p>
                            {/if}
                            {#if dropFiles[drop.id] && dropFiles[drop.id].length > 0}
                                <p>Archivos: <span class="font-semibold text-pink-800">{dropFiles[drop.id].length}</span></p>
                            {/if}
                        </div>
                    </div>
                {/each}
            </div>
        </div>
        <!-- Selected Drop Details and Map -->
        {#if selectedDrop}
            <div class="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 mt-6">
                <!-- Title -->
                <h2 class="text-2xl font-bold text-gray-800 mb-6">Ubicación de entrega - <span class="text-blue-500">{selectedDrop.delivery_method}</span></h2>
                <!-- Information Grid -->
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div class="space-y-4">
                        <div class="flex items-center">
                            <span class="font-semibold text-gray-700 min-w-[100px]">Método:</span>
                            <span class="text-gray-900">{selectedDrop.delivery_method}</span>
                        </div>
                        {#if selectedDrop.delivery_method === 'Repartidor'}
                            <div class="flex items-center">
                                <span class="font-semibold text-gray-700 min-w-[100px]">Repartidor:</span>
                                <span class="text-gray-900">{getDeliveryInfo(selectedDrop)}</span>
                            </div>
                            <div class="flex items-center">
                                <span class="font-semibold text-gray-700 min-w-[100px]">Vehículo:</span>
                                <span class="text-gray-900">{getVehicleInfo(selectedDrop)}</span>
                            </div>
                        {:else if selectedDrop.delivery_method === 'Transporte'}
                            <div class="flex items-center">
                                <span class="font-semibold text-gray-700 min-w-[100px]">Transporte:</span>
                                <span class="text-gray-900">{getTransportInfo(selectedDrop)}</span>
                            </div>
                            {#if selectedDrop.transporte_ref}
                                <div class="flex items-center">
                                    <span class="font-semibold text-gray-700 min-w-[100px]">Ref. Transporte:</span>
                                    <span class="text-gray-900">{selectedDrop.transporte_ref}</span>
                                </div>
                            {/if}
                        {/if}
                    </div>
                    <div class="space-y-4">
                        {#if selectedDrop.target_ref}
                            <div class="flex items-center">
                                <span class="font-semibold text-gray-700 min-w-[100px]">Ref. Entrega:</span>
                                <span class="text-gray-900">{selectedDrop.target_ref}</span>
                            </div>
                        {/if}
                        <div class="flex items-center">
                            <span class="font-semibold text-gray-700 min-w-[100px]">Escoltas:</span>
                            <span class="text-gray-900">{getEscoltaNames(selectedDrop.escoltas)}</span>
                        </div>
                        <div class="flex items-center">
                            <span class="font-semibold text-gray-700 min-w-[100px]">Archivos:</span>
                            <span class="text-gray-900">{getFileCount(selectedDrop.id)} archivos</span>
                        </div>
                    </div>
                </div>
                <!-- Map Container -->
                <div class="relative rounded-xl overflow-hidden border-2 border-blue-100 shadow-lg">
                    <div
                        id="delivery-map-canvas"
                        class="w-full rounded-xl border-none shadow-none"
                        style="height: 400px; min-height: 400px; background: linear-gradient(135deg, #e0f7fa 0%, #fff 100%);"
                    ></div>
                </div>
            </div>
        {/if}
    {/if}
</div>

<style>
    /* Custom styles for the map container */
    #delivery-map-canvas {
        background-color: #f8f9fa;
    }
    /* Hide loading overlay when map is ready */
    #delivery-map-canvas:not(:empty) + #map-loading {
        display: none;
    }
    .animate-spin-once {
        animation: spin 0.7s linear 1;
    }
    @keyframes spin {
        100% { transform: rotate(360deg); }
    }
</style>